import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Utensils, RefreshCw, Save, Link2, Trash2, AlertCircle, CheckCircle2 } from "lucide-react";

export default function ToastSync() {
  const [config, setConfig] = useState(null);
  const [rid, setRid] = useState("");
  const [saving, setSaving] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [pulling, setPulling] = useState(false);

  const load = async () => {
    try {
      const [c, mi, inv, m] = await Promise.all([
        api.get("/toast/config"),
        api.get("/toast/menu-items"),
        api.get("/inventory"),
        api.get("/toast/mappings"),
      ]);
      setConfig(c.data);
      setRid(c.data.restaurant_guid || "");
      setMenuItems(mi.data);
      setInventory(inv.data);
      setMappings(m.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  useEffect(() => { load(); }, []);

  const saveGuid = async () => {
    setSaving(true);
    try {
      await api.post("/toast/config", { restaurant_guid: rid });
      toast.success("Restaurant GUID saved");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const syncMenu = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/toast/menu/sync");
      toast.success(`Synced ${r.data.synced_count} menu items from Toast`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    setSyncing(false);
  };

  const pullOrders = async () => {
    setPulling(true);
    try {
      const r = await api.post("/toast/orders/sync");
      const { orders_fetched, selections_applied, selections_unmapped } = r.data;
      toast.success(
        `${orders_fetched} orders · ${selections_applied} sales applied · ${selections_unmapped} unmapped`
      );
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    setPulling(false);
  };

  const setMapping = async (toast_guid, inventory_id) => {
    try {
      if (!inventory_id) {
        const existing = mappings.find((m) => m.toast_guid === toast_guid);
        if (existing) {
          await api.delete(`/toast/mappings/${existing.id}`);
        }
      } else {
        await api.post("/toast/mappings", {
          toast_guid, inventory_id, quantity_per_sale: 1,
        });
      }
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const mapByGuid = Object.fromEntries(mappings.map((m) => [m.toast_guid, m]));
  const serverOk = config?.server_configured;

  return (
    <div className="space-y-6" data-testid="toast-root">
      <div>
        <div className="text-xs uppercase tracking-widest text-amber-500 font-mono-plex mb-2 flex items-center gap-2">
          <Utensils className="w-3 h-3" /> POS Integration
        </div>
        <h1 className="font-display text-4xl font-bold">Toast Sync</h1>
        <p className="text-slate-500 text-sm mt-1">
          Auto-deduct inventory when items are sold in Toast. Map Toast menu items to your SKUs, then sync.
        </p>
      </div>

      {!serverOk && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-5 flex gap-3" data-testid="toast-not-configured">
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold text-amber-400 mb-1">Toast API not configured on server</div>
            <div className="text-slate-400">
              To enable this integration, you (as the operator) need to:
              <ol className="list-decimal ml-5 mt-2 space-y-1">
                <li>Join the <span className="text-slate-200">Toast Partner Program</span> at pos.toasttab.com/partners</li>
                <li>Get sandbox <span className="font-mono-plex text-amber-400">TOAST_CLIENT_ID</span> and <span className="font-mono-plex text-amber-400">TOAST_CLIENT_SECRET</span></li>
                <li>Set <span className="font-mono-plex text-amber-400">TOAST_API_BASE_URL</span> to the hostname Toast provides</li>
                <li>Add all four to your production .env and redeploy</li>
              </ol>
            </div>
          </div>
        </div>
      )}

      <div className="surface-card rounded-xl p-6 space-y-4">
        <div className="font-display text-xl font-bold flex items-center gap-2">
          Restaurant configuration
          {serverOk && (
            <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded font-mono-plex">
              server ready · {config.environment}
            </span>
          )}
        </div>
        <label className="block">
          <div className="text-xs text-slate-400 mb-1">Toast Restaurant GUID</div>
          <div className="flex gap-2">
            <input
              value={rid}
              onChange={(e) => setRid(e.target.value)}
              placeholder="e.g. 47286e0a4-4fef-9230-b3dae11e7a9"
              data-testid="toast-guid"
              className="flex-1 bg-[#0a0c10] border border-white/10 rounded-md px-3 py-2 text-sm font-mono-plex outline-none focus:border-amber-500"
            />
            <button
              onClick={saveGuid}
              disabled={saving}
              data-testid="save-guid"
              className="btn-amber rounded-md px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-60"
            >
              <Save className="w-4 h-4" /> Save
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Ask your Toast contact for this — it identifies your restaurant location. Same for sandbox and production.
          </p>
        </label>
        {config?.last_sync_at && (
          <div className="text-xs text-slate-500 font-mono-plex">
            Last synced: {new Date(config.last_sync_at).toLocaleString()}
          </div>
        )}
      </div>

      <div className="flex gap-3">
        <button
          onClick={syncMenu}
          disabled={syncing || !serverOk || !rid}
          data-testid="sync-menu-btn"
          className="rounded-md px-4 py-2 text-sm border border-white/10 hover:bg-white/[0.03] flex items-center gap-2 disabled:opacity-40"
        >
          {syncing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Sync Toast menu
        </button>
        <button
          onClick={pullOrders}
          disabled={pulling || !serverOk || !rid || mappings.length === 0}
          data-testid="pull-orders-btn"
          className="btn-amber rounded-md px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-40"
        >
          {pulling ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Pull recent orders
        </button>
      </div>

      <div className="surface-card rounded-xl overflow-hidden" data-testid="mapping-table">
        <div className="p-5 border-b border-white/5 flex items-center justify-between">
          <div>
            <div className="font-display text-xl font-bold">Menu item mapping</div>
            <div className="text-xs text-slate-500 mt-1">
              Link each Toast menu item to a Barkeep inventory item. Sales will deduct the linked item's stock.
            </div>
          </div>
          <div className="text-xs text-slate-500 font-mono-plex">
            {menuItems.length} Toast items · {mappings.length} mapped
          </div>
        </div>
        {menuItems.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">
            No Toast menu items yet. Enter your Restaurant GUID above and click "Sync Toast menu".
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/5 text-slate-500 text-xs uppercase tracking-widest">
                <th className="text-left px-6 py-3">Toast Menu Item</th>
                <th className="text-left px-4 py-3">Group</th>
                <th className="text-left px-4 py-3">Mapped to Inventory</th>
                <th className="text-right px-6 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {menuItems.map((mi) => {
                const mapping = mapByGuid[mi.toast_guid];
                return (
                  <tr key={mi.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-6 py-3">
                      <div className="font-medium">{mi.name}</div>
                      <div className="text-xs text-slate-500 font-mono-plex">{mi.toast_guid?.slice(0, 8)}…</div>
                    </td>
                    <td className="px-4 py-3 text-slate-400">{mi.group || "—"}</td>
                    <td className="px-4 py-3">
                      <select
                        value={mapping?.inventory_id || ""}
                        onChange={(e) => setMapping(mi.toast_guid, e.target.value)}
                        className="w-full bg-[#0a0c10] border border-white/10 rounded-md px-2 py-1.5 text-xs outline-none focus:border-amber-500"
                        data-testid={`map-select-${mi.toast_guid}`}
                      >
                        <option value="">— not mapped —</option>
                        {inventory.map((i) => (
                          <option key={i.id} value={i.id}>{i.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-3 text-right">
                      {mapping ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-400">
                          <Link2 className="w-3 h-3" /> linked
                        </span>
                      ) : (
                        <span className="text-xs text-slate-500">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
